import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrophyIcon, UsersIcon, CalendarIcon } from '@heroicons/react/24/outline';
import SearchFilter from '../../components/SearchFilter';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const SportsPage = () => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    season: 'all',
    gender: 'all'
  });

  // Default sports data (will be replaced by API data when available)
  const defaultSports = [
    {
      id: 1,
      name: 'Football',
      slug: 'football',
      description: 'Competitive tackle football program building teamwork and athletic excellence.',
      season: 'fall',
      gender: 'boys',
      isActive: true,
      currentRecord: { wins: 8, losses: 2, ties: 0 },
      rosterCount: 35,
      coachName: 'Coach Johnson',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 2,
      name: 'Girls Soccer',
      slug: 'girls-soccer',
      description: 'Dynamic soccer program focused on skill development and competitive play.',
      season: 'fall',
      gender: 'girls',
      isActive: true,
      currentRecord: { wins: 6, losses: 3, ties: 1 },
      rosterCount: 22,
      coachName: 'Coach Martinez',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 3,
      name: 'Boys Soccer',
      slug: 'boys-soccer',
      description: 'Competitive soccer program developing technical skills and team strategy.',
      season: 'fall',
      gender: 'boys',
      isActive: true,
      currentRecord: { wins: 7, losses: 2, ties: 1 },
      rosterCount: 25,
      coachName: 'Coach Rodriguez',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 4,
      name: 'Girls Basketball',
      slug: 'girls-basketball',
      description: 'Fast-paced basketball program emphasizing fundamentals and teamwork.',
      season: 'winter',
      gender: 'girls',
      isActive: true,
      currentRecord: { wins: 12, losses: 3, ties: 0 },
      rosterCount: 15,
      coachName: 'Coach Thompson',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 5,
      name: 'Boys Basketball',
      slug: 'boys-basketball',
      description: 'Competitive basketball program building athletic skills and character.',
      season: 'winter',
      gender: 'boys',
      isActive: true,
      currentRecord: { wins: 10, losses: 4, ties: 0 },
      rosterCount: 18,
      coachName: 'Coach Williams',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 6,
      name: 'Track & Field',
      slug: 'track-field',
      description: 'Individual and team events developing speed, strength, and endurance.',
      season: 'spring',
      gender: 'coed',
      isActive: true,
      currentRecord: null,
      rosterCount: 45,
      coachName: 'Coach Davis',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 7,
      name: 'Cheer',
      slug: 'cheer',
      description: 'Spirit squad supporting all athletic programs with energy and enthusiasm.',
      season: 'year-round',
      gender: 'coed',
      isActive: true,
      currentRecord: null,
      rosterCount: 20,
      coachName: 'Coach Anderson',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 8,
      name: 'Dance',
      slug: 'dance',
      description: 'Competitive dance team showcasing creativity and athletic artistry.',
      season: 'year-round',
      gender: 'coed',
      isActive: true,
      currentRecord: null,
      rosterCount: 18,
      coachName: 'Coach Lee',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 9,
      name: 'Flag Football',
      slug: 'flag-football',
      description: 'Non-contact football program focusing on strategy and skill development.',
      season: 'spring',
      gender: 'coed',
      isActive: true,
      currentRecord: { wins: 5, losses: 1, ties: 0 },
      rosterCount: 28,
      coachName: 'Coach Brown',
      images: ['/api/placeholder/400/300']
    },
    {
      id: 10,
      name: 'Step Team',
      slug: 'step-team',
      description: 'Rhythmic stepping team combining athleticism with cultural expression.',
      season: 'year-round',
      gender: 'coed',
      isActive: true,
      currentRecord: null,
      rosterCount: 16,
      coachName: 'Coach Wilson',
      images: ['/api/placeholder/400/300']
    }
  ];

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/sports`);
      
      if (response.ok) {
        const data = await response.json();
        const sportsData = data.sports || [];
        
        // Fetch roster count for each sport from the database
        const sportsWithRosterCounts = await Promise.all(
          sportsData.map(async (sport) => {
            try {
              const playersResponse = await fetch(`${API_BASE_URL}/players/sport/${sport.id}`);
              if (playersResponse.ok) {
                const playersData = await playersResponse.json();
                return {
                  ...sport,
                  rosterCount: playersData.players ? playersData.players.length : 0
                };
              } else {
                return { ...sport, rosterCount: 0 };
              }
            } catch (error) {
              console.error(`Error fetching players for sport ${sport.id}:`, error);
              return { ...sport, rosterCount: 0 };
            }
          })
        );
        
        setSports(sportsWithRosterCounts);
      } else {
        // Use default sports data if API is not available
        setSports(defaultSports);
      }
    } catch (error) {
      console.error('Error fetching sports:', error);
      // Use default sports data as fallback
      setSports(defaultSports);
    } finally {
      setLoading(false);
    }
  };

  const getWinPercentage = (record) => {
    if (!record || (!record.wins && !record.losses && !record.ties)) return null;
    const totalGames = record.wins + record.losses + record.ties;
    if (totalGames === 0) return null;
    return Math.round((record.wins / totalGames) * 100);
  };

  const getSeasonColor = (season) => {
    switch (season) {
      case 'fall': return 'bg-orange-100 text-orange-800';
      case 'winter': return 'bg-blue-100 text-blue-800';
      case 'spring': return 'bg-green-100 text-green-800';
      case 'year-round': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getGenderColor = (gender) => {
    switch (gender) {
      case 'boys': return 'bg-blue-100 text-blue-800';
      case 'girls': return 'bg-pink-100 text-pink-800';
      case 'coed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSports = sports.filter(sport => {
    const searchMatch = searchTerm === '' || 
      sport.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sport.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sport.coachName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const seasonMatch = filters.season === 'all' || sport.season === filters.season;
    const genderMatch = filters.gender === 'all' || sport.gender === filters.gender;
    
    return searchMatch && seasonMatch && genderMatch && sport.isActive;
  });

  const filterOptions = {
    season: [
      { value: 'fall', label: 'Fall' },
      { value: 'winter', label: 'Winter' },
      { value: 'spring', label: 'Spring' },
      { value: 'year-round', label: 'Year Round' }
    ],
    gender: [
      { value: 'boys', label: 'Boys' },
      { value: 'girls', label: 'Girls' },
      { value: 'coed', label: 'Co-ed' }
    ]
  };



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-lg text-white mb-4">Our Sports Programs</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            East End Prep offers 10 competitive sports programs designed to develop 
            athletic excellence, character, and teamwork in our student-athletes.
          </p>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={filters}
            onFilterChange={setFilters}
            filterOptions={filterOptions}
            placeholder="Search sports, coaches, or descriptions..."
          />
          <div className="text-sm text-gray-600 text-right">
            Showing {filteredSports.length} of {sports.length} sports
          </div>
        </div>
      </section>

      {/* Sports Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner spinner-primary w-8 h-8"></div>
            </div>
          ) : filteredSports.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredSports.map((sport) => (
                <div key={sport.id} className="card card-hover">
                  {/* Sport Image */}
                  <div className="aspect-w-16 aspect-h-9">
                    {sport.signedUrl || sport.featuredImage || sport.images?.[0] ? (
                      <img
                        src={sport.signedUrl || sport.featuredImage || sport.images?.[0]}
                        alt={sport.name}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-full h-48 bg-gradient-to-br from-primary to-secondary flex items-center justify-center" 
                      style={{ display: sport.signedUrl || sport.featuredImage || sport.images?.[0] ? 'none' : 'flex' }}
                    >
                      <div className="text-center text-white">
                        <TrophyIcon className="w-12 h-12 mx-auto mb-2 opacity-80" />
                        <p className="text-sm font-medium">{sport.name}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Sport Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      <Link 
                        to={`/sports/${sport.slug}`}
                        className="hover:text-primary transition-colors duration-200"
                      >
                        {sport.name}
                      </Link>
                    </h3>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`badge ${getSeasonColor(sport.season)}`}>
                        {sport.season}
                      </span>
                      <span className={`badge ${getGenderColor(sport.gender)}`}>
                        {sport.gender}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {sport.description}
                    </p>

                    {/* Stats */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Roster:</span>
                        <span className="font-medium flex items-center">
                          <UsersIcon className="w-4 h-4 mr-1" />
                          {sport.rosterCount} athletes
                        </span>
                      </div>
                      
                      {sport.coachName && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Coach:</span>
                          <span className="font-medium">{sport.coachName}</span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <Link
                        to={`/sports/${sport.slug}`}
                        className="btn-outline w-full text-center inline-flex items-center justify-center"
                      >
                        <TrophyIcon className="w-4 h-4 mr-2" />
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Sports Found</h3>
              <p className="text-gray-500">Try adjusting your filters to see more sports programs.</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-secondary py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-md text-white mb-6">
            Ready to Join a Team?
          </h2>
          
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Discover your passion and develop your skills with our championship sports programs. 
            Every Eagle has a place to soar.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-secondary text-lg px-8 py-3 inline-flex items-center justify-center"
            >
              <CalendarIcon className="w-5 h-5 mr-2" />
              Schedule a Visit
            </Link>
            
            <Link
              to="/events"
              className="btn-outline bg-white border-white text-secondary hover:bg-blue-50 text-lg px-8 py-3 inline-flex items-center justify-center"
            >
              View Upcoming Games
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SportsPage;